import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { COVER_DOC_LIMITS, COVER_EXPORT_PX, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { KIT_FONT_FACES, themeType } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { FontPicker } from './FontPicker';
import { InspectorRow } from './controls/InspectorRow';
import { Segmented } from './controls/Segmented';
import { Slider } from './controls/Slider';
import { Swatches } from './controls/Swatches';
import { useTextPatch } from './useTextPatch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TextStyleFieldsProps {
  box: CoverTextBox;
}

const px = (share: number): number => Math.round(share * COVER_EXPORT_PX);

/** The first block of the text inspector (design cover-editor b1): font, size, weight, colour, align, case, spacing, line. */
const TextStyleFieldsComponent: React.FC<TextStyleFieldsProps> = ({ box }) => {
  const intl = useIntl();
  const { state, readOnly } = useCoverEditor();
  const patch = useTextPatch(box.id);
  const [minWeight, maxWeight] = KIT_FONT_FACES[box.fontId].weights;
  const themeFontId = useMemo(
    () => themeType({ themeId: state.doc.themeId, fontOverride: null }).fonts.display,
    [state.doc.themeId]
  );
  const aligns = useMemo(
    () => [
      { value: 'left' as const, label: intl.formatMessage(coverEditorMessages.alignLeft) },
      { value: 'center' as const, label: intl.formatMessage(coverEditorMessages.alignCentre) },
      { value: 'right' as const, label: intl.formatMessage(coverEditorMessages.alignRight) },
    ],
    [intl]
  );
  const cases = useMemo(
    () => [
      { value: 'none' as const, label: 'Aa' },
      { value: 'upper' as const, label: 'AA' },
      { value: 'lower' as const, label: 'aa' },
    ],
    []
  );
  const [minSize, maxSize] = COVER_DOC_LIMITS.sizeShare;

  return (
    <div className={styles.inspectorGrid}>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textFont)}>
        <FontPicker value={box.fontId} themeFontId={themeFontId} disabled={readOnly} onChange={(font) => patch(font)} />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textSize)}>
        <Slider
          label={intl.formatMessage(coverEditorMessages.textSize)}
          value={px(box.sizeShare)}
          min={px(minSize)}
          max={px(maxSize)}
          display={String(px(box.sizeShare))}
          disabled={readOnly}
          onChange={(value) => patch({ sizeShare: value / COVER_EXPORT_PX }, 'size')}
        />
      </InspectorRow>
      {minWeight !== maxWeight && (
        <InspectorRow label={intl.formatMessage(coverEditorMessages.textWeight)}>
          <Slider
            label={intl.formatMessage(coverEditorMessages.textWeight)}
            value={box.weight}
            min={minWeight}
            max={maxWeight}
            step={50}
            display={String(box.weight)}
            disabled={readOnly}
            onChange={(weight) => patch({ weight }, 'weight')}
          />
        </InspectorRow>
      )}
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textColour)}>
        <Swatches
          label={intl.formatMessage(coverEditorMessages.textColour)}
          palette={state.doc.palette}
          value={box.colour}
          disabled={readOnly}
          onChange={(colour) => patch({ colour }, 'colour')}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textAlign)}>
        <Segmented
          label={intl.formatMessage(coverEditorMessages.textAlign)}
          options={aligns}
          value={box.align}
          disabled={readOnly}
          onChange={(align) => patch({ align })}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textCase)}>
        <Segmented
          label={intl.formatMessage(coverEditorMessages.textCase)}
          options={cases}
          value={box.textCase}
          disabled={readOnly}
          onChange={(textCase) => patch({ textCase })}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textSpacing)}>
        <Slider
          label={intl.formatMessage(coverEditorMessages.textSpacing)}
          value={Math.round(box.tracking * 100)}
          min={COVER_DOC_LIMITS.tracking[0] * 100}
          max={60}
          display={`${Math.round(box.tracking * 100)}%`}
          disabled={readOnly}
          onChange={(value) => patch({ tracking: value / 100 }, 'tracking')}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textLine)}>
        <Slider
          label={intl.formatMessage(coverEditorMessages.textLine)}
          value={Math.round(box.lineHeight * 100)}
          min={COVER_DOC_LIMITS.lineHeight[0] * 100}
          max={200}
          step={5}
          display={box.lineHeight.toFixed(2)}
          disabled={readOnly}
          onChange={(value) => patch({ lineHeight: value / 100 }, 'lineHeight')}
        />
      </InspectorRow>
    </div>
  );
};

export const TextStyleFields = TypedMemo(TextStyleFieldsComponent);
